'use client'

import { useState, useEffect, useId, useRef } from 'react'
import { trackEvent } from '@/lib/track-event'
import { canonicalizeDomainInput, sanitizeHref } from '@/lib/sanitize'
import { peekVisitorId } from '@/lib/analytics/visitor-id'
import {
  getAttributionEnvelope,
  getFirstTouchUtm,
} from '@/lib/analytics/first-touch-utm'
import { RETAINER_AUDITS } from '@/lib/paddle/pricing'

/**
 * Guided free-check launcher for the `/platform` hero (Profound-style).
 *
 *   email + domain → OTP ("Step 1: verify") → "Radar is running…" → result page.
 *
 * Steps 1-2 reuse the existing OTP endpoints (request-code / verify-code).
 * Step 4 is server-orchestrated: it POSTs the domain to /api/platform/free-check
 * which runs the audit BEHIND THE SCENES (brand-index pattern), persists a
 * teaser-locked result, emails the link, and returns a reportId. Step 5 is a
 * router.push to /platform/check/<id> — the user lands on a real result page.
 *
 * Every step renders in the design overhaul's form markup (the reference
 * `form.audit` from public/pixelmojo-rebuild/pixelmojo-platform.html, styled
 * by the page sheet and src/app/reference-fixes.css). Until 2026-10-09 the
 * component also kept the pre-overhaul Tailwind UI as a second render path,
 * used only by the retired platform-client.tsx; the logic above was always
 * shared by both.
 */

type State =
  | 'init'
  | 'form'
  | 'sending'
  | 'code'
  | 'verifying'
  | 'sent'
  | 'used'
  | 'capped'

interface CapInfo {
  domain?: string
  message?: string
  cachedResultUrl?: string | null
  upsellUrl?: string
}

const TOOL = 'ai-readiness'

// Delegates to the shared canonicalizer so the field accepts the same inputs
// the API will accept, and rejects the ones it won't. Previously this passed
// anything URL-parseable, so "example.com?ref=x" reached the API (and the lead
// record) with the query string still attached.
function validateDomain(value: string): boolean {
  return canonicalizeDomainInput(value) !== null
}

function friendlyError(reason: unknown): string {
  switch (reason) {
    case 'invalid':
      return 'That code is incorrect. Check your email and try again.'
    case 'expired':
      return 'That code expired. Send a new one below.'
    case 'too_many_attempts':
      return 'Too many wrong attempts. Send a new code below.'
    case 'rate_limit':
      return 'Too many requests. Wait a minute and try again.'
    case 'daily_cap':
      return 'You have hit the daily verification limit for this email.'
    case 'disposable_email':
      return 'Temporary email addresses are not supported. Use a real email.'
    case 'invalid_email':
      return 'That email looks invalid. Try another.'
    case 'invalid_tool':
      return 'Unsupported tool. Refresh the page.'
    case 'send_failed':
      return "We couldn't send the code to that address. Check it and try again."
    case 'server_error':
      return 'Our server hit a snag. Try again in a moment.'
    default:
      return 'Something went wrong. Try again.'
  }
}

/** How long "Send a new code" stays disabled after each send (PIX-47). */
const RESEND_COOLDOWN_MS = 30_000

interface FreeCheckLauncherProps {
  /**
   * Primary submit label. Defaults to the /platform wording; the paid-ad
   * landing (/platform/start) overrides it so the CTA matches the ad creative.
   * Copy-only — no behavioural difference between the two surfaces.
   */
  submitLabel?: string
}

const REF_ARROW = (
  <svg
    className='arrow'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.8'
    aria-hidden='true'
  >
    <path d='M5 12h14M13 6l6 6-6 6' />
  </svg>
)

export default function FreeCheckLauncher({
  submitLabel = 'Get My Free Snapshot',
}: FreeCheckLauncherProps = {}) {
  const [state, setState] = useState<State>('init')
  // Stable ids so each control carries a real <label> and the one error
  // paragraph can be announced and pointed at by aria-describedby. The fields
  // were placeholder-only until now, so a screen reader announced two unnamed
  // text boxes and browser autofill had nothing to target.
  const fieldIdBase = useId()
  const emailFieldId = `${fieldIdBase}-email`
  const domainFieldId = `${fieldIdBase}-domain`
  const domainHintId = `${fieldIdBase}-domain-hint`
  const errorFieldId = `${fieldIdBase}-error`
  const codeFieldId = `${fieldIdBase}-code`
  const [email, setEmail] = useState('')
  const [domain, setDomain] = useState('')
  const [code, setCode] = useState('')
  // ready = the server already grants a run (paid session, founder, or a
  // still-valid free credit) → skip OTP.
  const [ready, setReady] = useState(false)
  const [cap, setCap] = useState<CapInfo | null>(null)
  // Set once the server-side run returns; enables the "view now" link on the
  // confirmation screen (the report is otherwise delivered by email).
  const [resultUrl, setResultUrl] = useState('')
  const [emailed, setEmailed] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  /**
   * Which control the current errorMsg belongs to, or null for errors that
   * belong to no single field (network, server, rate limit).
   *
   * Without this every control derived aria-invalid from the shared errorMsg,
   * so a malformed domain announced the email as invalid too and sent
   * assistive-technology users to fix a field that was fine (Qodo round 1,
   * finding 3).
   */
  const [errorField, setErrorField] = useState<
    'email' | 'domain' | 'code' | null
  >(null)
  // Server ran the paid-user sign-in recovery for this email (uniform flag —
  // an email went out only if the account actually qualifies).
  const [recoveryInitiated, setRecoveryInitiated] = useState(false)
  // The unticked product-news box (PIX-47), separate from the code, the
  // report and the audit follow-ups.
  const [productNews, setProductNews] = useState(false)
  // Where the code went, with the box frozen at that moment: a resend reuses
  // both, so it can never flip the choice.
  const [codeSent, setCodeSent] = useState<{
    email: string
    productNews: boolean
  } | null>(null)
  const [resending, setResending] = useState(false)
  const [resendCooling, setResendCooling] = useState(false)
  const [resent, setResent] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current)
    },
    []
  )
  // Domain arrived via ?domain= (Vector chat hands off the domain the visitor
  // already typed). Tracked so we can tell chat-sourced runs apart downstream.
  const [prefilled, setPrefilled] = useState(false)

  // Read from window.location rather than useSearchParams: the seed is only
  // ever needed after mount, so this keeps the read local to the one effect
  // that uses it and avoids adding a Suspense boundary around the launcher.
  useEffect(() => {
    const seeded = new URLSearchParams(window.location.search).get('domain')
    if (!seeded) return
    const canonical = canonicalizeDomainInput(seeded)
    if (!canonical) return
    setDomain(canonical)
    setPrefilled(true)
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await fetch(`/api/tools/access/me?tool=${TOOL}`, {
          cache: 'no-store',
        })
        const data = await res.json().catch(() => ({}))
        if (cancelled) return
        if (data?.isPaid || data?.hasCredit) {
          setReady(true)
          setState('form')
          return
        }
        if (data?.verifiedEmail && typeof data.verifiedEmail === 'string') {
          setEmail(data.verifiedEmail)
          setState('used')
          return
        }
        setState('form')
      } catch {
        if (!cancelled) setState('form')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  async function runFreeCheck() {
    // Single choke point: all three entry paths (bypass, post-OTP, already
    // ready) land here, so canonicalizing once guarantees only a bare hostname
    // ever leaves the client — whatever is sitting in the input field.
    const target = canonicalizeDomainInput(domain)
    if (!target) {
      setErrorMsg('Enter a valid domain (e.g. example.com).')
      setErrorField('domain')
      setState('form')
      return
    }
    // First-touch attribution for the whole journey. getFirstTouchUtm() is
    // itself consent-aware: it returns null (and clears stale data) whenever
    // analytics are not currently allowed, so a consent-denied visitor sends
    // nothing here, on the event, or to the API.
    const attribution = getFirstTouchUtm() ?? {}
    trackEvent('free_check_started', { prefilled, ...attribution })
    // Profound-style: confirm immediately and deliver the report by email. The
    // audit runs server-side; we don't block the screen on it or show the
    // result inline. A "view now" link appears once the run returns.
    setState('sent')
    setErrorMsg(null)
    setErrorField(null)
    try {
      const res = await fetch('/api/platform/free-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // peek, never mint: starting an audit must not create
        // attribution for a visitor who was never LLM-referred.
        // The envelope adds the DISPOSITION alongside the values, so the
        // server can tell a consent-denied visitor apart from an untagged
        // organic landing. Both used to arrive as an identical row of NULLs.
        body: JSON.stringify({
          url: target,
          visitorId: peekVisitorId(),
          ...getAttributionEnvelope(),
        }),
        keepalive: true,
      })
      const data = await res.json().catch(() => ({}))
      if (res.status === 429 && data.error === 'domain_cap') {
        setCap(data as CapInfo)
        setState('capped')
        return
      }
      if (res.status === 403) {
        // Credit no longer valid — fall back to re-verify.
        setReady(false)
        setErrorMsg('Please verify your email to run the check.')
        setErrorField(null)
        setState('form')
        return
      }
      if (!res.ok || !data?.resultUrl) {
        setErrorMsg(data.message || 'Could not run the check. Try again.')
        setErrorField(null)
        setState('form')
        return
      }
      trackEvent('free_check_completed', attribution)
      setEmailed(Boolean(data.emailed))
      setResultUrl(data.resultUrl as string)
    } catch {
      setErrorMsg('Network error. Please try again.')
      setErrorField(null)
      setState('form')
    }
  }

  function startCooldown() {
    if (cooldownTimer.current) clearTimeout(cooldownTimer.current)
    setResendCooling(true)
    cooldownTimer.current = setTimeout(
      () => setResendCooling(false),
      RESEND_COOLDOWN_MS
    )
  }

  /**
   * One code request, first or resend. A resend reuses `codeSent`, so the
   * product-news choice on the new code row is the one the visitor made. A
   * failed resend keeps the visitor on the code step.
   */
  async function sendCode(
    target: { email: string; productNews: boolean },
    resend: boolean
  ) {
    if (resend) setResending(true)
    else setState('sending')
    setErrorMsg(null)
    setErrorField(null)
    setNotice(null)
    const failed = (message: string) => {
      setErrorMsg(message)
      setErrorField(null)
      setState(resend ? 'code' : 'form')
    }
    try {
      const res = await fetch('/api/tools/access/request-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: target.email,
          tool: TOOL,
          productNews: target.productNews,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (data?.reason === 'already_used') {
        setRecoveryInitiated(Boolean(data?.recoveryInitiated))
        setState('used')
        return
      }
      if (!res.ok) {
        failed(friendlyError(data?.reason))
        return
      }
      if (data?.bypass) {
        setReady(true)
        void runFreeCheck()
        return
      }
      setCodeSent(target)
      startCooldown()
      setState('code')
      if (resend) {
        // Only the newest code verifies (verifyOtp reads the newest row).
        setCode('')
        setResent(true)
        setNotice(
          'New code sent. Use the newest one; earlier codes stop working.'
        )
      } else {
        setResent(false)
        trackEvent('platform_otp_requested', getFirstTouchUtm() ?? {})
      }
    } catch {
      failed('Network error. Try again.')
    } finally {
      if (resend) setResending(false)
    }
  }

  function requestCode() {
    void sendCode({ email: email.trim().toLowerCase(), productNews }, false)
  }

  function resendCode() {
    if (!codeSent || resending || resendCooling) return
    void sendCode(codeSent, true)
  }

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErrorMsg(null)
    setErrorField(null)
    if (!ready && (!email.trim() || !email.includes('@'))) {
      setErrorMsg('Enter a valid email.')
      setErrorField('email')
      return
    }
    if (!validateDomain(domain)) {
      setErrorMsg('Enter a valid domain (e.g. example.com).')
      setErrorField('domain')
      return
    }
    if (ready) {
      void runFreeCheck()
      return
    }
    trackEvent('platform_email_submitted', getFirstTouchUtm() ?? {})
    requestCode()
  }

  async function handleCodeSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!/^\d{6}$/.test(code.trim())) {
      setErrorMsg('Enter the 6-digit code from your email.')
      setErrorField('code')
      return
    }
    setState('verifying')
    setErrorMsg(null)
    setErrorField(null)
    setNotice(null)
    try {
      const res = await fetch('/api/tools/access/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // The address the code was SENT to, not whatever the email box
          // holds now: the two can only disagree through a stale step.
          email: codeSent?.email ?? email.trim(),
          code: code.trim(),
          tool: TOOL,
          // Customer-journey key (PIX-45). Null without analytics consent.
          visitorId: peekVisitorId(),
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data?.ok) {
        // After a resend an older code fails as "incorrect"; say why.
        setErrorMsg(
          resent && data?.reason === 'invalid'
            ? 'That code is incorrect. Use the newest code we sent; earlier codes stop working.'
            : friendlyError(data?.reason)
        )
        setErrorField('code')
        setState('code')
        return
      }
      trackEvent('platform_otp_verified', getFirstTouchUtm() ?? {})
      setReady(true)
      void runFreeCheck()
    } catch {
      setErrorMsg('Network error. Try again.')
      setErrorField(null)
      setState('code')
    }
  }

  // ---------- render ----------
  const sending = state === 'sending'
  const verifying = state === 'verifying'
  const errorLine = errorMsg ? (
    <p
      id={errorFieldId}
      role='alert'
      className='audit__fine'
      style={{ color: 'var(--pink)' }}
    >
      {errorMsg}
    </p>
  ) : null

  if (state === 'sent' || state === 'capped' || state === 'used') {
    const reportReady = Boolean(resultUrl)
    const title =
      state === 'sent'
        ? reportReady
          ? 'Your Radar scan is ready'
          : 'Preparing your Radar scan…'
        : state === 'capped'
          ? `Free checks used for ${cap?.domain ?? 'this domain'}`
          : 'You have used your free check'
    const body =
      state === 'sent'
        ? !reportReady
          ? `Hang tight while Radar scans ${
              domain
                .trim()
                .replace(/^https?:\/\//, '')
                .replace(/\/+$/, '') || 'your site'
            }.`
          : emailed
            ? `We've emailed the link to ${email}. Open it any time, or view it now.`
            : 'Your audit is ready.'
        : state === 'capped'
          ? 'This domain has reached its free limit. Unlock repeat audits and all 13 tools with Radar.'
          : `Unlock the full report for $5, or go Pro at $199/mo for ${RETAINER_AUDITS} full audits a month across every tool.`
    const href =
      state === 'sent'
        ? reportReady
          ? (sanitizeHref(resultUrl) ?? '/platform')
          : null
        : state === 'capped'
          ? (sanitizeHref(cap?.upsellUrl ?? '') ?? '/platform#pricing')
          : '/platform#pricing'
    const label =
      state === 'sent'
        ? 'View your result'
        : state === 'capped'
          ? 'See Radar plans'
          : 'See pricing'
    return (
      <div className='audit' role='status'>
        <div style={{ display: 'grid', gap: 10 }}>
          <h3>{title}</h3>
          {state === 'used' && recoveryInitiated && (
            <p className='audit__fine'>
              If this email has Radar credits, purchases, or an active
              subscription, we&apos;ll send a sign-in link. Please check your
              inbox and spam folder.
            </p>
          )}
          <p className='audit__fine'>{body}</p>
        </div>
        {href && (
          <a className='btn btn--accent' href={href}>
            {label} {REF_ARROW}
          </a>
        )}
        {state === 'capped' && cap?.cachedResultUrl && (
          <a
            className='audit__fine'
            href={sanitizeHref(cap.cachedResultUrl) ?? '/platform'}
          >
            View the existing report for {cap.domain}
          </a>
        )}
        {state === 'used' && (
          <p className='audit__fine'>
            Already purchased? <a href='/platform/sign-in'>Sign in</a>
          </p>
        )}
      </div>
    )
  }

  if (state === 'code' || state === 'verifying') {
    return (
      <form className='audit' onSubmit={handleCodeSubmit}>
        <div className='audit__row'>
          <label htmlFor={codeFieldId}>
            6-digit code sent to {email}
            <input
              id={codeFieldId}
              type='text'
              inputMode='numeric'
              autoComplete='one-time-code'
              maxLength={6}
              value={code}
              onChange={e => {
                setCode(e.target.value.replace(/\D/g, ''))
                if (errorMsg) setErrorMsg(null)
              }}
              placeholder='123456'
              autoFocus
              disabled={verifying}
              aria-invalid={errorField === 'code' ? true : undefined}
              aria-describedby={errorMsg ? errorFieldId : undefined}
            />
            <span className='audit__hint'>
              Didn&apos;t get it? Check your spam folder.
            </span>
          </label>
        </div>
        <button
          className='btn btn--accent'
          type='submit'
          disabled={verifying || !/^\d{6}$/.test(code.trim())}
        >
          {verifying ? 'Checking…' : 'Run my check'} {REF_ARROW}
        </button>
        {errorLine}
        {notice && !errorMsg && (
          <p role='status' className='audit__fine'>
            {notice}
          </p>
        )}
        <p className='audit__fine'>
          <button
            type='button'
            onClick={resendCode}
            disabled={resending || resendCooling || verifying}
          >
            {resending ? 'Sending…' : 'Send a new code'}
          </button>
          {' · '}
          <button
            type='button'
            onClick={() => {
              setCode('')
              setErrorMsg(null)
              setNotice(null)
              setState('form')
            }}
            disabled={resending || verifying}
          >
            Use a different email
          </button>
        </p>
      </form>
    )
  }

  // 'init' (access check in flight), 'form' and 'sending' all show the
  // reference form; it is inert until the access check settles.
  const busy = state === 'init' || sending
  return (
    <form className='audit' onSubmit={handleFormSubmit}>
      <div className='audit__row'>
        {!ready && (
          <label htmlFor={emailFieldId}>
            Work email
            <input
              id={emailFieldId}
              type='email'
              name='email'
              value={email}
              onChange={e => {
                setEmail(e.target.value)
                if (errorMsg) setErrorMsg(null)
              }}
              placeholder='your@company.com'
              autoComplete='email'
              required
              disabled={busy}
              aria-invalid={errorField === 'email' ? true : undefined}
              aria-describedby={errorMsg ? errorFieldId : undefined}
            />
          </label>
        )}
        <label htmlFor={domainFieldId}>
          Website domain
          <input
            id={domainFieldId}
            type='text'
            name='domain'
            value={domain}
            onChange={e => {
              setDomain(e.target.value)
              if (errorMsg) setErrorMsg(null)
            }}
            placeholder='yourdomain.com'
            inputMode='url'
            autoComplete='url'
            required
            disabled={busy}
            aria-invalid={errorField === 'domain' ? true : undefined}
            aria-describedby={
              errorMsg ? `${errorFieldId} ${domainHintId}` : domainHintId
            }
          />
          <span className='audit__hint' id={domainHintId}>
            Just the domain, for example example.com
          </span>
        </label>
      </div>
      <button className='btn btn--accent' type='submit' disabled={busy}>
        {sending ? 'Sending…' : submitLabel} {REF_ARROW}
      </button>
      {errorLine}
      {!ready && (
        <>
          <label className='audit__consent'>
            <input
              type='checkbox'
              name='news'
              checked={productNews}
              onChange={e => setProductNews(e.target.checked)}
              disabled={busy}
            />
            <span>Also send me occasional Radar product news.</span>
          </label>
          <p className='audit__fine'>
            We email your 6-digit code and your report, and may send a few short
            follow-ups about this audit. Unsubscribe any time. No credit card is
            required.
          </p>
        </>
      )}
    </form>
  )
}
